/*
 * HIG8 (GOALS/GOALS-DESIGN.html#hig8): a refusal banner shows a plain sentence
 * first and the exact machine string second. Apple's Writing page: write the
 * error close to the problem, say what happened and what to do next, never a
 * code as the headline. The Pocket art direction says the same in our words:
 * "exact machine strings can look exact, but ordinary language must look
 * ordinary." Nothing here rewrites the machine string — it is preserved
 * verbatim as the detail so the door's exact refusal stays readable.
 */

import { PREVIOUS_REPLY_UNCONFIRMED_MESSAGE, WORLD_SIGN_IN_ENDED_MESSAGES } from "@cleo/contracts";

export interface RefusalCopy {
  /** The sentence a person reads first. Sentence case, ends with a period. */
  readonly headline: string;
  /** The exact machine string, or null when the message already read as prose. */
  readonly detail: string | null;
}

const MACHINE_PATTERN = /REFUSED|\[[A-Z0-9_]{6,}\]|\b[a-z]+_[a-z_]+\b\)|_[A-Z]{3,}/;

/** Known refusal shapes, matched on the machine string, mapped to a person's sentence. */
const KNOWN: ReadonlyArray<readonly [RegExp, string]> = [
  // Can 73: a founder who reaches the Members room before its instructions are published in this World (an old
  // link, the command menu) reads that it is not ready, never the World's instruction refusal as the headline.
  [
    /room '?world-members'? has no published instructions|INSTRUCTION_ROOM_REQUIRED[\s\S]*world-members/,
    "Members isn't ready in this World yet. Nothing was sent.",
  ],
  // WSET4: the person's automatic context rules are applied before a Send.
  // When they cannot be, the message is not sent, and the person reads why
  // (24 September 2026: a member's Send showed only a passing "Try again").
  [
    /automatic-addition rules[\s\S]*cockpit\.context/,
    "Your message was not sent. Your automatic context rules would add items to it, and this World has not given you permission to change context. Turn those rules off in Settings under World, Automatic context, or ask the World's Founder for that permission.",
  ],
  [
    /AUTO_TIER_BUDGET/,
    "Your message was not sent. One of your automatic context rules names more items than one message can take. Narrow that rule or turn it off in Settings under World, Automatic context.",
  ],
  [
    /automatic-addition rules could not be applied/,
    "Your message was not sent, because your automatic context rules could not be applied. The World's reason is below.",
  ],
  [
    // A context change (the rail's Include, a drop) needs cockpit.context, which an ordinary member does
    // not hold (GOALS CX11; W-1600dcfd). The automatic-addition case above keeps its own sentence.
    /lacks live World membership or cockpit\.context/,
    "This World has not given you permission to change what this session's context includes, so nothing was changed. Ask the World's Founder for that permission.",
  ],
  [
    /lacks live World membership(?! or)/,
    "Your message was not sent. This World no longer lists you as a member. Sign in again, or ask the World's Founder.",
  ],
  [
    /REFUSED[\s\S]*EXECUTION_CHANGED/,
    "The World changed while this message was being prepared. Send again to retry with the current state.",
  ],
  [
    /could not be frozen[\s\S]*INSTRUCTION_EXECUTION_UNKNOWN/,
    "This session was started by someone else, so its instructions could not be frozen for you. Open a new thread to continue here.",
  ],
  [
    /could not be frozen/,
    "The context for this message could not be frozen. Review the selection in the rail and send again.",
  ],
  [
    /REFUSED[\s\S]*NOT_ADMITTED|not yet admitted/,
    "This action is not admitted for you yet. Ask the World's manager for access, or continue in a session you opened.",
  ],
  [
    /REFUSED[\s\S]*BOUNDARY/,
    "That item is outside this message's boundary. Select it in the rail and send the next message.",
  ],
  [/rate.?limit|\b429\b/i, "The provider is busy right now. Wait a moment and send again."],
  [
    /unauthori[sz]ed|\b(?:401|403)\b/i,
    "This account is not signed in for that provider. Sign in again from Settings, then retry.",
  ],
];

/**
 * W-a02f7279: a thread keeps the error its last Send recorded, so what an
 * earlier build wrote there is shown again at every launch. Those builds
 * recorded an unconfirmed last reply in internal words ("Provider validation
 * failed in ProviderRuntimeIngestion.waitForWorldCompletion: The previous World
 * execution remains unresolved: …"). Each reads as the sentence this build
 * records for the same event, so the person gets the same words and the same
 * sign-in door. The exact string stays in the banner's tooltip.
 */
const RECORDED_BY_EARLIER_BUILDS: ReadonlyArray<readonly [RegExp, string]> = [
  [
    /previous World execution remains unresolved[\s\S]*(?:rejected this grant|revoked or expired|not signed in|sign in)|previous message ended, because this Mac's World access needs refreshing/i,
    WORLD_SIGN_IN_ENDED_MESSAGES.beforeConfirm,
  ],
  [
    /previous World execution remains unresolved|could not confirm how your previous message ended/,
    PREVIOUS_REPLY_UNCONFIRMED_MESSAGE,
  ],
];

/**
 * The provider service's error envelope puts an internal operation name first
 * (Errors.ts ProviderValidationError / ProviderAdapterValidationError). The
 * sentence after it is what the person reads; the operation is for the log.
 */
const PROVIDER_ENVELOPE = /^Provider (?:adapter )?validation failed (?:\([^)]*\) )?in [\w.]+: /;

function sentence(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  if (!trimmed) return "Something did not work.";
  const first = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(first) ? first : `${first}.`;
}

/**
 * Split a thread error into a plain headline and the preserved machine detail.
 * Prose that already reads as a sentence passes through with no detail line.
 */
export function humanizeRefusal(message: string): RefusalCopy {
  const raw = message.trim();
  for (const [pattern, headline] of RECORDED_BY_EARLIER_BUILDS) {
    if (pattern.test(raw)) return { headline, detail: null };
  }
  const issue = raw.replace(PROVIDER_ENVELOPE, "");
  if (issue !== raw) {
    const inner = humanizeRefusal(issue);
    return { headline: inner.headline, detail: inner.detail === null ? null : raw };
  }
  for (const [pattern, headline] of KNOWN) {
    if (pattern.test(raw)) return { headline, detail: raw };
  }
  if (!MACHINE_PATTERN.test(raw)) return { headline: sentence(raw), detail: null };
  // Unknown machine shape: the first clause before the code becomes the sentence.
  const clause = raw.split(/[:(\[]/)[0] ?? raw;
  const headline = sentence(clause.length >= 12 ? clause : "Something did not work");
  return {
    headline: headline === sentence(raw) ? "Something did not work." : headline,
    detail: raw,
  };
}

/** Why a Send's context preparation refused (see neucleos/contextResolution FreezeRefusal). */
export interface SendPreparationRefusal {
  readonly reason:
    | "coordinate-unavailable"
    | "selection-unavailable"
    | "selection-mismatch"
    | "source-unavailable"
    | "sign-in-ended";
  /** The World's own words, when it gave a reason. */
  readonly detail?: string | undefined;
}

export interface SendPreparationRefusalCopy {
  readonly title: string;
  readonly description: string;
  /**
   * What stays on screen after the notice fades, as the conversation's error
   * banner (which shows it through humanizeRefusal), or null when trying again
   * is the whole remedy.
   */
  readonly threadError: string | null;
}

/**
 * A Send that cannot run says why, in words a person reads, and a refusal the
 * World explained stays on screen: a notice that fades while the message sits
 * back in the box reads as "Send does nothing" (24 September 2026).
 */
export function sendPreparationRefusalCopy(
  refusal: SendPreparationRefusal,
): SendPreparationRefusalCopy {
  const detail = refusal.detail?.trim() || null;
  switch (refusal.reason) {
    // W-a02f7279, W-c0ac93da: the conversation keeps the sentence on screen,
    // with the one-press sign-in beside it; the message waits in the box.
    case "sign-in-ended":
      return {
        title: "Sign in again to send",
        description: WORLD_SIGN_IN_ENDED_MESSAGES.beforeSend,
        threadError: WORLD_SIGN_IN_ENDED_MESSAGES.beforeSend,
      };
    case "selection-mismatch":
      return {
        title: "Context changed",
        description: "Review the refreshed Context selection and send again.",
        threadError: null,
      };
    case "source-unavailable":
      return {
        title: "Selected source is unavailable",
        description:
          "Review Context. Remove the unavailable folder, then select an available folder if needed.",
        threadError: null,
      };
    case "coordinate-unavailable":
      return {
        title: "Context is not ready",
        description: "This conversation has no World session to send in.",
        threadError: null,
      };
    case "selection-unavailable":
      if (detail === null)
        return {
          title: "Context is not ready",
          description: "The World selection could not be read before Send. Try again.",
          threadError: null,
        };
      return {
        title: "Message not sent",
        description: humanizeRefusal(detail).headline,
        threadError: detail,
      };
  }
}

/*
 * "This World does not offer that tool (yet)." Signing in only says who the
 * person is; the World decides a member's tools live on every call, so a new
 * tool reaches an existing sign-in at once (27 September 2026). A tool can
 * still be refused as absent: the Desk's `grant_stale` (the tool is not a
 * member tool in this Cleo's registry, which in practice does not happen) and
 * the World's `tool_not_on_grant` (the World is older than this Cleo, or it is
 * suspended). Signing in again changes neither, so no copy for these two may
 * ask the person to sign in or refresh access.
 */

/** Whether a typed refusal kind means the World does not offer the tool. */
export function isToolNotOfferedRefusal(kind: unknown): boolean {
  return kind === "grant_stale" || kind === "tool_not_on_grant";
}

/**
 * The same, read from the words alone, for callers whose client drops the
 * kind: the Desk's "'<tool>' is not a tool a World member's session holds."
 * and the World's "tool '<tool>' is not on this grant".
 */
export function isToolNotOfferedReason(reason: string): boolean {
  return /is not a tool a World member's session holds|is not on this grant/i.test(reason);
}

/** The notice when the World refuses a change to this session's context (the rail's Include, a drop). */
export function contextChangeRefusalCopy(reason: string): { title: string; description: string } {
  return { title: "Context update needs attention", description: humanizeRefusal(reason).headline };
}

/*
 * A member presses Connect and the World refuses because their World Grant
 * lacks the capability the connect needs (W: a Calendar Connect showed
 * "REFUSED [..]: the Actor lacks active capability 'booking.manage'"). The
 * member cannot fix that and the capability code means nothing to them, so the
 * row says what they cannot do and who can. Read from the words alone, because
 * the lane returns the World's string with its kind dropped to a transport
 * label. Covers the Grant check ("lacks active capability 'x'"), the session
 * check ("lacks live World membership or x") and a delegation that "does not
 * carry 'x'".
 */
const MISSING_PERMISSION =
  /\b(?:lacks (?:active capability|live World membership or)|does not carry)\s+'?[a-z][a-z0-9]*(?:\.[a-z][a-z0-9_]*)+'?/i;

/** Whether the World refused because the member's Grant lacks a capability. */
export function isMissingPermissionReason(reason: string): boolean {
  return MISSING_PERMISSION.test(reason);
}

/**
 * The one sentence a Connect row shows. `integration` is the name the member
 * sees on the card ("Calendar"). Null when the refusal is something else, so
 * the caller keeps showing that reason.
 */
export function connectPermissionRefusalCopy(integration: string, reason: string): string | null {
  if (!isMissingPermissionReason(reason)) return null;
  return `Your World's founder hasn't let members connect ${integration} here. Ask them to allow it.`;
}
